import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaTrash, FaCopy, FaCheck, FaImage, FaFolder } from 'react-icons/fa';
import { storage, ref, uploadBytes, getDownloadURL, deleteObject, listAll } from '../../firebase';

const ImageManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('projects');
  const [copiedUrl, setCopiedUrl] = useState(null);

  const folders = [
    { id: 'projects', label: 'Project Images' },
    { id: 'profile', label: 'Profile Images' },
    { id: 'content', label: 'Content Images' },
    { id: 'misc', label: 'Miscellaneous' },
  ];

  useEffect(() => {
    fetchImages();
  }, [selectedFolder]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const imagesRef = ref(storage, selectedFolder);
      const imagesList = await listAll(imagesRef);
      
      const imageUrls = await Promise.all(
        imagesList.items.map(async (item) => {
          const url = await getDownloadURL(item);
          return {
            name: item.name,
            url: url,
            path: item.fullPath,
            ref: item
          };
        })
      );
      
      setImages(imageUrls);
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        // Create a unique filename
        const timestamp = Date.now();
        const filename = `${timestamp}_${file.name}`;
        const storageRef = ref(storage, `${selectedFolder}/${filename}`);
        
        // Upload file
        await uploadBytes(storageRef, file);
      }
      
      alert(`Successfully uploaded ${files.length} file(s)!`);
      fetchImages(); // Refresh the list
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imagePath, imageName) => {
    if (!confirm(`Are you sure you want to delete "${imageName}"?`)) return;

    try {
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      alert('Image deleted successfully!');
      fetchImages(); // Refresh the list
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    }
  };

  const copyToClipboard = (url, name) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(name);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Image Manager</h2>
        <div className="flex items-center gap-3">
          <input
            type="file"
            id="imageUpload"
            multiple
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
          />
          <label
            htmlFor="imageUpload"
            className={`flex items-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:shadow-lg transition-all ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <FaUpload />
            {uploading ? 'Uploading...' : 'Upload Images'}
          </label>
        </div>
      </div>

      {/* Folder Selection */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {folders.map((folder) => (
          <button
            key={folder.id}
            onClick={() => setSelectedFolder(folder.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all ${
              selectedFolder === folder.id
                ? 'bg-violet-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <FaFolder />
            {folder.label}
          </button>
        ))}
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-16 bg-gray-800 rounded-lg">
          <FaImage className="mx-auto text-6xl text-gray-600 mb-4" />
          <p className="text-gray-400">No images in this folder yet.</p>
          <p className="text-gray-500 text-sm mt-2">Upload your first image to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <AnimatePresence>
            {images.map((image, index) => (
              <motion.div
                key={image.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-800 rounded-lg overflow-hidden group"
              >
                {/* Image Preview */}
                <div className="relative aspect-video bg-gray-900">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => copyToClipboard(image.url, image.name)}
                      className="p-3 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-all"
                      title="Copy URL"
                    >
                      {copiedUrl === image.name ? <FaCheck /> : <FaCopy />}
                    </button>
                    <button
                      onClick={() => handleDelete(image.path, image.name)}
                      className="p-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-3">
                  <p className="text-gray-300 text-sm font-medium truncate" title={image.name}>
                    {image.name}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => copyToClipboard(image.url, image.name)}
                      className="flex-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded transition-all"
                    >
                      {copiedUrl === image.name ? 'Copied!' : 'Copy URL'}
                    </button>
                    <a
                      href={image.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs bg-violet-500 hover:bg-violet-600 text-white px-3 py-1 rounded transition-all"
                    >
                      View
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Storage Stats */}
      <div className="bg-gray-800 p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Total Images in {selectedFolder}</span>
          <span className="text-white font-semibold">{images.length}</span>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <h4 className="text-blue-400 font-semibold mb-2">💡 How to Use Images</h4>
        <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
          <li>Upload images to the appropriate folder</li>
          <li>Click "Copy URL" to get the image link</li>
          <li>Paste the URL in your project/content fields</li>
          <li>Images are automatically optimized for web</li>
        </ol>
      </div>
    </div>
  );
};

export default ImageManager;
